import { TranCategoryKey } from '../models/transaction-metadata.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { TransactionMetadataSource } from '../services/transaction-metadata.service'
import { readConditionField } from './loadout-condition'

/**
 * **The prototype calls this a "loadout".**
 *
 * `docs/prototypes/pval-26341/Transactions Panel 2.0.dc.html` names the concept `loadout` throughout
 * — `createLoadoutItems`, `txnLoadoutFor`, `glLoadout`. This code calls it a tran category instead:
 * the same idea in CWAN's own vocabulary (`tranTypeCode`, `tranData`), which is what the Postgres
 * table and the metadata API will carry. When a comment cites a prototype identifier by name it is
 * left spelled the prototype's way, because it names a real function in that file.
 *
 * Which curated dialog context a row opens into — or, when none applies, exactly why not.
 *
 * There is deliberately **no default branch**: an unreachable default is a default a later refactor
 * reaches, and silently rendering the Listed-trade tran category for a swap would show the wrong
 * fields against a real trade. Create mode never calls this — it is handed its tran category
 * directly.
 */

/**
 * Why the modal refuses to open. Split deliberately: a value no condition claims (`unmappedValue`)
 * and a row carrying nothing any condition reads (`missingValue`) have different one-line fixes —
 * one in the seed, one on the read API.
 *
 * Neither name mentions an instrument type any more. `instrumentType` is one ENFUSION column; the
 * reason a row blocked has to be sayable for a source that has never heard of it.
 */
export type TranCategoryBlockReason = 'unmappedValue' | 'missingValue'

export interface TranCategoryResolved {
  kind: 'tranCategory'
  tranCategory: TranCategoryKey
}

export interface TranCategoryBlocked {
  kind: 'blocked'
  reason: TranCategoryBlockReason
  /**
   * The document fields the detail line names: the one routed field carrying an unclaimed value,
   * or every routed field when the row carried none.
   *
   * Raw document keys, not prose. No source authors a label for them — they are read-API row
   * columns, absent from the document's own `fields[]` — and the key is what a config author edits
   * to fix the block, so quoting it is more useful than humanising it.
   */
  fields: readonly string[]
  /** The offending value, verbatim. Absent when the row carried none. */
  detail?: string
}

export type TranCategoryResolution = TranCategoryResolved | TranCategoryBlocked

const text = (value: unknown): string =>
  value == null ? '' : String(value).trim()

/**
 * Resolve the dialog context for a row of any source.
 *
 *  a. a tran category's `loadoutCondition` matches the row -> that tran category.
 *  b. nothing matches, and the row carries a value in a field the document routes on -> blocked,
 *     naming that field and value.
 *  c. nothing matches, and the row carries none of them -> blocked, naming the fields it expected.
 *
 * **The document owns the rule, entirely** — including which fields the rule reads. Each tran
 * category carries a condition saying when it opens (`in instrumentType (…)` for an ENFUSION trade,
 * `isNotNull gLEntryId` for a journal entry) and the evaluator walks it. Nothing here names a field:
 * `instrumentType` is one ENFUSION column, and a resolver that asserted it could not report
 * truthfully on a source that has never carried one. (b) and (c) therefore ask the metadata for the
 * routed fields rather than holding a constant of their own.
 *
 * (c) deliberately does NOT fall back to a default schema. A row nothing claims is either reference
 * data that should never have reached a trade dialog, or a rule not yet authored — and opening the
 * wrong dialog on a real trade is worse than opening none.
 */
export function resolveTranCategory(
  row: WorkbenchRow,
  metadata: TransactionMetadataSource,
): TranCategoryResolution {
  // (a)
  const tranCategory = metadata.tranCategoryFor(row)
  if (tranCategory) return { kind: 'tranCategory', tranCategory }

  // (b) / (c) — the fields the DOCUMENT routes on, never a constant held here. ENFUSION's
  // conditions name `instrumentType` and `gLEntryId`; the next source names its own, and this
  // reads whichever of them the row actually carried.
  const routed = metadata.conditionFields()
  const carried = routed
    .map(field => ({ field, value: text(readConditionField(row, field)) }))
    .find(entry => entry.value.length > 0)

  return carried
    ? {
        kind: 'blocked',
        reason: 'unmappedValue',
        fields: [carried.field],
        detail: carried.value,
      }
    : { kind: 'blocked', reason: 'missingValue', fields: routed }
}

/** Heading shown when the modal refuses to open, per reason. */
export const BLOCK_HEADINGS: Readonly<Record<TranCategoryBlockReason, string>> =
  {
    unmappedValue: 'This transaction is not configured yet',
    missingValue: 'This transaction has nothing to route on',
  }

/**
 * The secondary note beneath the heading, naming the document key and value so the config bug is
 * diagnosable rather than merely reported. Mirrors the prototype's failed-state note.
 *
 * Both arms name raw document keys. For `missingValue` that is the whole point: it tells a reader
 * which columns the document expects, which is the one thing a row carrying none of them cannot
 * show them.
 */
export function blockDetailLine(blocked: TranCategoryBlocked): string | null {
  if (blocked.reason === 'unmappedValue') {
    const [field] = blocked.fields
    return field && blocked.detail ? `${field}: ${blocked.detail}` : null
  }
  return blocked.fields.length > 0
    ? `Expected one of: ${blocked.fields.join(', ')}`
    : null
}
